import React from 'react';

import { NarrativeState } from '../../types/narrative';

interface Arrow {
  from: string;
  to: string;
}

interface Position {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function useArrows(
  arrows: Arrow[],
  domNodePositions: Map<string, Position>,
  nodeAbsolutePositions: Map<string, Position>,
  dimensions: { width: number; height: number },
  narrativeState: NarrativeState,
  totalHeight: number
) {
  return React.useMemo(() => {
    if (dimensions.width <= 0 || arrows.length === 0) return null;

    return (
      <svg
        className="absolute inset-0 pointer-events-none"
        style={{ width: '100%', height: '100%', zIndex: 1 }}
        viewBox={`0 0 ${dimensions.width} ${totalHeight}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {arrows.map((arrow, idx) => {
          const hasFromDom = domNodePositions.has(arrow.from);
          const hasToDom = domNodePositions.has(arrow.to);

          let fromPos: Position | undefined;
          let toPos: Position | undefined;

          if (hasFromDom && hasToDom) {
            fromPos = domNodePositions.get(arrow.from);
            toPos = domNodePositions.get(arrow.to);
          } else {
            fromPos = nodeAbsolutePositions.get(arrow.from);
            toPos = nodeAbsolutePositions.get(arrow.to);
          }

          if (!fromPos || !toPos) {
            console.warn(`Missing position for arrow ${arrow.from} -> ${arrow.to}`);
            return null;
          }

          const startX = fromPos.x + fromPos.width / 2;
          const startY = fromPos.y + fromPos.height;
          const endX = toPos.x + toPos.width / 2;
          const endY = toPos.y;
          const midY = (startY + endY) / 2;

          const fromNode = narrativeState.nodes[arrow.from];
          const strokeColor =
            fromNode?.completionStatus === 'complete'
              ? '#059669'
              : fromNode?.completionStatus === 'partial'
                ? '#4f46e5'
                : '#64748b';

          return (
            <g key={`${arrow.from}-${arrow.to}-${idx}`}>
              <path
                d={`M ${startX} ${startY} C ${startX} ${midY}, ${endX} ${midY}, ${endX} ${endY}`}
                stroke={strokeColor}
                strokeWidth="2"
                fill="none"
                opacity="0.6"
              />
              <path
                d={`M ${endX - 4} ${endY - 8} L ${endX} ${endY} L ${endX + 4} ${endY - 8}`}
                stroke={strokeColor}
                strokeWidth="2"
                fill="none"
                opacity="0.6"
              />
            </g>
          );
        })}
      </svg>
    );
  }, [arrows, domNodePositions, nodeAbsolutePositions, dimensions, narrativeState, totalHeight]);
}
